import React, { useEffect, useState } from 'react';
import { Badge, Dropdown, Nav, Navbar, NavDropdown } from 'react-bootstrap';
import { PersonCircle, Youtube } from 'react-bootstrap-icons';
import { DropdownSubmenu, NavDropdownMenu } from 'react-bootstrap-submenu';
import { useHistory, useLocation } from 'react-router';
import { toast } from 'react-toastify';
import { makeApiRequests } from '../helpers/api';
import {
  ENDPOINTS,
  ROLE_ADMIN,
  ROLE_INSTALLER,
  ROLE_INVENTORY_MANAGER,
  ROLE_SALES,
  ROLE_SALES_AND_INVENTORY_MANAGER,
  ROLE_SALES_MANAGER,
  ROLE_SERVICE,
  ROLE_SUPER_ADMIN,
  ROLE_TECH
} from '../helpers/constants';
import { downloadFileFromString } from '../helpers/global';
import { isAdmin, userHasAnyRole } from '../helpers/roleHelpers';
import { adminEntities } from './admin/constants';
import Logo from './common/Logo';
import MobileNavigation from './MobileNavigation';

const getUserFirstName = () => {
  const name = localStorage.getItem('user-name') || '';
  if (name) return name.split(' ')[0];

  return name;
};

const Header = ({ noMenu = false, currentSectionVideoMeta, featuredVideoMeta, onOpenVideoPlayerChange }) => {
  const history = useHistory();
  const location = useLocation();
  const [pendingCourseCount, setPendingCourseCount] = useState(0);
  const [expanded, setExpanded] = useState(false);
  useEffect(() => setExpanded(false), [location.pathname]);
  useEffect(() => {
    if (!expanded || !window.matchMedia || !window.matchMedia('(max-width: 991px)').matches) return undefined;
    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const escape = event => { if (event.key === 'Escape') setExpanded(false); };
    window.addEventListener('keydown', escape);
    return () => { document.body.style.overflow = oldOverflow; window.removeEventListener('keydown', escape); };
  }, [expanded]);

  const onCoursesSection = location.pathname.startsWith('/sales-academy') || location.pathname === '/training-resources';

  useEffect(() => {
    if (noMenu) return;

    let cancelled = false;
    (async () => {
      const { response, error } = await makeApiRequests({
        endpoint: ENDPOINTS.COURSES_PENDING_COUNT,
        method: 'GET',
        authSilent: true
      });
      if (cancelled || error) return;

      setPendingCourseCount(response?.pendingCount || 0);
    })();

    return () => {
      cancelled = true;
    };
  }, [noMenu, onCoursesSection]);

  const LinkItem = ({ dropdown = false, title, path, onClick, isLink = true }) => {
    return dropdown ? (
      <NavDropdown.Item onClick={onClick} href={path} active={isLink && location.pathname === path}>
        {title}
      </NavDropdown.Item>
    ) : (
      <Nav.Link onClick={onClick} href={path} active={isLink && location.pathname === path}>
        {title}
      </Nav.Link>
    );
  };

  const logout = async () => {
    const { error } = await makeApiRequests({
      endpoint: ENDPOINTS.LOGOUT,
      method: 'POST',
      authSilent: true
    });
    if (error) {
      toast.error(error);
      return;
    }

    const preservedWidths = localStorage.getItem('pnl-column-widths');

    Object.keys(localStorage).forEach(key => {
      if (key !== 'pnl-column-widths') {
        localStorage.removeItem(key);
      }
    });

    // Restore PnL column widths if they aren't already present
    if (preservedWidths && !localStorage.getItem('pnl-column-widths')) {
      localStorage.setItem('pnl-column-widths', preservedWidths);
    }

    history.push('/login');
  };

  const ProfileMenuDropdownItems = ({ history }) => (
    <>
      <Dropdown.Toggle size="sm" variant="outline-dark rounded">
        <PersonCircle size={18} className="mr-2 align-text-top" />
        {getUserFirstName()}
      </Dropdown.Toggle>
      <Dropdown.Menu>
        <Dropdown.Item onClick={() => history.push('/profile')}>View Profile</Dropdown.Item>
        <Dropdown.Item onClick={() => logout(history)}>Logout</Dropdown.Item>
      </Dropdown.Menu>
    </>
  );

  const VideoMenuDropdownItems = () => (
    <>
      <Dropdown.Toggle size="sm" variant="outline-dark rounded">
        <Youtube size={18} className="align-text-top" />
      </Dropdown.Toggle>
      <Dropdown.Menu>
        {featuredVideoMeta?.videoUrls?.length > 0 && (
          <Dropdown.Item onClick={() => onOpenVideoPlayerChange('featured')}>About this app</Dropdown.Item>
        )}
        {currentSectionVideoMeta?.videoUrls?.length > 0 && (
          <Dropdown.Item onClick={() => onOpenVideoPlayerChange('section')}>Videos for this page</Dropdown.Item>
        )}
      </Dropdown.Menu>
    </>
  );

  const downloadContractedBuyersCSV = async () => {
    toast.info('File will be downloaded shortly...');

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.CONTRACTS_DOWNLOAD_CSV,
      method: 'GET',
      authSilent: true
    });

    if (error) {
      toast.error(error);
      return;
    }

    const { csv } = response;
    downloadFileFromString({ data: csv, fileName: 'Contracts.csv' });
  };

  const navigation = (
            <Nav className="mr-auto d-none d-lg-flex">
              {!userHasAnyRole(ROLE_INVENTORY_MANAGER) && (
                <NavDropdown
                  title="Contracts"
                  active={location.pathname.startsWith('/contract') || location.pathname === '/pipeline'}
                >
                  <LinkItem path="/contract/search" title="Search" dropdown />

                  {isAdmin() && (
                    <LinkItem path="/contract/create" title="Create" dropdown />
                  )}

                  {userHasAnyRole(
                    ROLE_SUPER_ADMIN,
                    ROLE_ADMIN,
                    ROLE_SALES,
                    ROLE_SALES_MANAGER,
                    ROLE_SERVICE,
                    ROLE_INSTALLER,
                    ROLE_SALES_AND_INVENTORY_MANAGER
                  ) && <LinkItem path="/pipeline" title="Pipeline" dropdown />}
                </NavDropdown>
              )}

              {userHasAnyRole(ROLE_SERVICE) && (
                <NavDropdown title="Service Requests" active={location.pathname.startsWith('/service-requests')}>
                  <LinkItem path="/service-requests/new" title="New requests" dropdown />
                  <LinkItem path="/service-requests/my-claims" title="My claims" dropdown />
                  <LinkItem path="/service-requests/browse" title="Browse requests" dropdown />
                </NavDropdown>
              )}

              {!userHasAnyRole(
                ROLE_INSTALLER,
                ROLE_TECH,
                ROLE_SERVICE,
                ROLE_INVENTORY_MANAGER,
                ROLE_SALES_AND_INVENTORY_MANAGER
              ) && <LinkItem path="/service-requests/browse" title="Service requests" />}

              {userHasAnyRole(
                ROLE_SUPER_ADMIN,
                ROLE_ADMIN,
                ROLE_SALES,
                ROLE_SALES_MANAGER,
                ROLE_SERVICE,
                ROLE_INVENTORY_MANAGER,
                ROLE_SALES_AND_INVENTORY_MANAGER
              ) && <LinkItem path="/dochub/print-groups" title="Doc Hub" />}

              {userHasAnyRole(
                ROLE_SALES,
                ROLE_SALES_MANAGER,
                ROLE_ADMIN,
                ROLE_SUPER_ADMIN,
                ROLE_SALES_AND_INVENTORY_MANAGER
              ) && (
                <NavDropdown
                  title="CRM"
                  active={
                    location.pathname.startsWith('/crm') ||
                    location.pathname.startsWith('/messages') ||
                    location.pathname === '/pipeline/crm' ||
                    location.pathname.startsWith('/conversation-bank') ||
                    location.pathname === '/top-producer-view'
                  }
                >
                  {userHasAnyRole(
                    ROLE_SALES,
                    ROLE_SALES_MANAGER,
                    ROLE_SALES_AND_INVENTORY_MANAGER,
                    ROLE_ADMIN,
                    ROLE_SUPER_ADMIN
                  ) && <LinkItem path="/top-producer-view" title="Top Producer View" dropdown />}
                  <LinkItem path="/messages" title="Messages &amp; Campaigns" dropdown />
                  <LinkItem path="/crm/create" title="Create Contact" dropdown />
                  <LinkItem path="/crm/calendar" title="Store Calendar" dropdown />
                  <LinkItem path="/pipeline/crm" title="Pipeline" dropdown />
                  <LinkItem path="/conversation-bank" title="Conversation Bank" dropdown />
                </NavDropdown>
              )}

              {userHasAnyRole(ROLE_TECH) && <LinkItem path="/walkthrough/search" title="Walkthrough" />}

              {userHasAnyRole(ROLE_SALES_MANAGER, ROLE_SALES, ROLE_SALES_AND_INVENTORY_MANAGER) && (
                <>
                  <LinkItem path="/reports/dealboard" title="Dealboard" />
                  <LinkItem path="/reports/sales" title="Sales" />
                </>
              )}

              {userHasAnyRole(ROLE_INSTALLER) && <LinkItem path="/setup/search" title="Set up" />}
              {!userHasAnyRole(ROLE_INVENTORY_MANAGER) && <LinkItem path="/schedule" title="Schedule" />}
              {userHasAnyRole(ROLE_SUPER_ADMIN, ROLE_ADMIN, ROLE_SERVICE) && (
                <NavDropdownMenu
                  title="Reports"
                  className="mb-2 mb-lg-0"
                  active={location.pathname.startsWith('/reports')}
                >
                  {isAdmin() && (
                    <>
                      <LinkItem path="/reports/form-t" title="Form T" dropdown />
                      <LinkItem path="/reports/sol" title="SOL" dropdown />

                      <DropdownSubmenu title="Rebate">
                        <LinkItem path="/reports/request-rebate" title="Request Rebate" dropdown />
                        <LinkItem path="/reports/record-receipt" title="Record Receipt" dropdown />
                        <LinkItem path="/reports/rebate-report" title="Rebate Report" dropdown />
                        <LinkItem path="/reports/explore-rebate" title="Explore Rebate" dropdown />
                      </DropdownSubmenu>
                      <DropdownSubmenu title="AC Rebate">
                        <LinkItem path="/reports/request-ac-rebate" title="Request AC Rebate" dropdown />
                        <LinkItem path="/reports/record-ac-receipt" title="Record AC Receipt" dropdown />
                        <LinkItem path="/reports/ac-rebate-report" title="AC Rebate Report" dropdown />
                        <LinkItem path="/reports/explore-ac-rebate" title="Explore AC Rebate" dropdown />
                      </DropdownSubmenu>

                      <LinkItem path="/reports/mhit-report" title="MHIT Report" dropdown />
                      <LinkItem path="/reports/delivery-setup-report" title="Delivery/Setup Report" dropdown />
                      <LinkItem path="/reports/floorplan-update" title="Floorplan Update" dropdown />
                      <LinkItem path="/reports/curtailments" title="Curtailments" dropdown />
                      <LinkItem path="/reports/cash-deal" title="Cash Deal" dropdown />
                      <DropdownSubmenu title="Payroll">
                        <LinkItem path="/reports/record-payroll" title="Record Payroll" dropdown />
                        <LinkItem path="/reports/explore-payrolls" title="Explore Payroll" dropdown />
                      </DropdownSubmenu>
                      <LinkItem path="/reports/pnl" title="P&L" dropdown />
                      <LinkItem path="/reports/dealboard" title="Dealboard" dropdown />
                      <LinkItem path="/reports/lead" title="Leads" dropdown />
                      <LinkItem path="/reports/sales" title="Sales" dropdown />
                    </>
                  )}

                  <DropdownSubmenu title="Billback">
                    <LinkItem path="/reports/request-billback" title="Request Billback" dropdown />
                    <LinkItem path="/reports/record-billback-receipt" title="Record Billback Receipt" dropdown />
                    <LinkItem path="/reports/explore-billback" title="Explore Billback" dropdown />
                  </DropdownSubmenu>
                  <LinkItem path="/reports/service" title="Service" dropdown />
                </NavDropdownMenu>
              )}

              {userHasAnyRole(
                ROLE_SUPER_ADMIN,
                ROLE_ADMIN,
                ROLE_SALES,
                ROLE_SALES_MANAGER,
                ROLE_SERVICE,
                ROLE_INVENTORY_MANAGER,
                ROLE_SALES_AND_INVENTORY_MANAGER
              ) && (
                <NavDropdown
                  title="Inventory"
                  active={location.pathname.startsWith('/inventory') || location.pathname === '/pipeline/inventory'}
                >
                  <LinkItem path="/inventory/browse" title="Browse Inventory" dropdown />
                  <LinkItem path="/inventory/create" title="Create Inventory Item" dropdown />
                  <LinkItem path="/pipeline/inventory" title="Pipeline" dropdown />
                  <LinkItem path="/inventory/site-map" title="Site Map" dropdown />
                </NavDropdown>
              )}

              {userHasAnyRole(
                ROLE_SALES,
                ROLE_SALES_MANAGER,
                ROLE_SUPER_ADMIN,
                ROLE_ADMIN,
                ROLE_SERVICE,
                ROLE_SALES_AND_INVENTORY_MANAGER
              ) && (
                <NavDropdown title="Tools" active={location.pathname.startsWith('/utilities')}>
                  {userHasAnyRole(
                    ROLE_SALES,
                    ROLE_SALES_MANAGER,
                    ROLE_SUPER_ADMIN,
                    ROLE_ADMIN,
                    ROLE_SALES_AND_INVENTORY_MANAGER
                  ) && (
                    <>
                      <LinkItem path="/utilities/delivery-set-calculator" title="Del & set calculator" dropdown />
                      <LinkItem
                        path="#"
                        title="Download Contracted Buyers"
                        dropdown
                        onClick={downloadContractedBuyersCSV}
                      />
                    </>
                  )}
                  {userHasAnyRole(ROLE_SALES, ROLE_SALES_MANAGER, ROLE_SUPER_ADMIN, ROLE_ADMIN, ROLE_SERVICE, ROLE_SALES_AND_INVENTORY_MANAGER) && <>
                    <LinkItem path="/utilities/commission-sheet" title="Commission Sheet" dropdown />
                    <LinkItem path="/utilities/sales-agreement" title="Sales Agreement" dropdown />
                  </>}
                </NavDropdown>
              )}
              <LinkItem
                path="/training-resources"
                title={
                  <>
                    Training &amp; Resources
                    {pendingCourseCount > 0 && (
                      <Badge pill variant="danger" className="ml-1" style={{ fontSize: 11 }}>
                        {pendingCourseCount}
                      </Badge>
                    )}
                  </>
                }
              />
              {userHasAnyRole(
                ROLE_SALES,
                ROLE_SALES_MANAGER,
                ROLE_ADMIN,
                ROLE_SUPER_ADMIN,
                ROLE_SALES_AND_INVENTORY_MANAGER
              ) && <LinkItem path="/conversation-bank" title="Conversation Bank" />}

              {isAdmin() ? (
                <NavDropdown title="Admin" active={location.pathname.startsWith('/admin')}>
                  <LinkItem path="/admin/users" title="Users" dropdown />
                  <LinkItem path="/admin/sales-location" title="Sales Location" dropdown />
                  <LinkItem path="/admin/commission-sheet-options" title="Commission/P&L options" dropdown />
                  <LinkItem path="/admin/manufacturer" title="Manufacturers" dropdown />
                  <LinkItem path="/admin/section-video" title="Section Videos" dropdown />

                  <LinkItem path="/admin/app-choices" title="App Choices" dropdown />
                  {adminEntities.map(e => (
                    <LinkItem key={e.route} path={`/admin${e.route}`} title={e.name} dropdown />
                  ))}
                  <LinkItem path="/admin/lot-sets" title="Lot-Set Options" dropdown />
                  <LinkItem path="/admin/dochub-fields" title="DocHub Fields" dropdown />
                  <LinkItem path="/admin/custom-entities" title="Custom Entities" dropdown />
                  <LinkItem path="/admin/dochub-variables" title="DocHub Variables" dropdown />
                  <LinkItem path="/admin/manage-sales-academy" title="Manage Training & Resources" dropdown />
                  <LinkItem path="/admin/conversation-bank" title="Manage Conversation Bank" dropdown />
                </NavDropdown>
              ) : (
                <NavDropdown
                  title="More"
                  active={
                    location.pathname.startsWith('/admin/manufacturer') ||
                    location.pathname.startsWith('/admin/lender')
                  }
                >
                  <LinkItem path="/admin/manufacturer" title="Manufacturers" dropdown />
                  <LinkItem path="/admin/lender" title="Lenders" dropdown />
                </NavDropdown>
              )}
            </Nav>
  );

  return (
    <Navbar expanded={expanded} onToggle={setExpanded} bg="light" fixed="top" expand="lg" className="app-header" style={{ fontSize: 15, zIndex: 999 }}>
      <Navbar.Brand className="mr-4" href="/">
        <Logo small />
      </Navbar.Brand>
      {!noMenu && (
        <>
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          <Navbar.Collapse id="basic-navbar-nav">
            <MobileNavigation pathname={location.pathname} expanded={expanded} name={getUserFirstName()} onClose={() => setExpanded(false)} onLogout={logout} onProfile={() => history.push('/profile')}
              onPageVideo={currentSectionVideoMeta?.videoUrls?.length ? () => onOpenVideoPlayerChange('section') : undefined}
              onAppVideo={featuredVideoMeta?.videoUrls?.length ? () => onOpenVideoPlayerChange('featured') : undefined}>{navigation}</MobileNavigation>
            {navigation}
            <div className="d-none d-lg-block">
              {(currentSectionVideoMeta?.videoUrls?.length > 0 || featuredVideoMeta?.videoUrls?.length > 0) && (
                <>
                  <Dropdown className="d-none d-lg-inline-block mr-1" style={{ zIndex: 999 }} drop="left">
                    <VideoMenuDropdownItems />
                  </Dropdown>
                  <Dropdown className="d-inline-block d-lg-none mt-1" drop="up" style={{ zIndex: 999 }}>
                    <VideoMenuDropdownItems />
                  </Dropdown>
                </>
              )}

              <Dropdown className="d-none d-lg-inline-block" style={{ zIndex: 999 }} drop="left">
                <ProfileMenuDropdownItems history={history} />
              </Dropdown>
              <Dropdown className="d-inline-block d-lg-none ml-2 mt-1" drop="up" style={{ zIndex: 999 }}>
                <ProfileMenuDropdownItems history={history} />
              </Dropdown>
            </div>
          </Navbar.Collapse>
        </>
      )}
    </Navbar>
  );
};

export default Header;
